import type { useUpdateAccountAddress } from '@/services/db/account/mutations';
import { isUnitedStates, isZipCodeIgnored } from './address-options';
import type { CreditCardFormAddress } from './credit-card-form-submit';
import type { BillingFieldErrors } from './credit-card-form-validation';

type AccountAddressSource = {
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
};

/** Builds the form's address shape from the account's stored address fields. */
export function addressFromAccount(account: AccountAddressSource): CreditCardFormAddress {
  return {
    addressLine1: account.address_line1 ?? '',
    addressLine2: account.address_line2 ?? '',
    city: account.city ?? '',
    state: account.state ?? '',
    zipCode: account.zip_code ?? '',
    country: account.country ?? '',
  };
}

/** Applies a single address-field change, clearing state/zip when the country makes them irrelevant. */
export function applyAddressChange(
  current: CreditCardFormAddress,
  field: keyof CreditCardFormAddress,
  value: string,
): CreditCardFormAddress {
  const next = { ...current, [field]: value };
  if (field === 'country' && !isUnitedStates(value)) next.state = '';
  if (field === 'country' && isZipCodeIgnored(value)) next.zipCode = '';
  return next;
}

/** Persists the billing address on the account (a non-US country carries no state). */
export function persistAccountAddress(
  updateAccount: ReturnType<typeof useUpdateAccountAddress>['mutateAsync'],
  next: CreditCardFormAddress,
): Promise<void> {
  return updateAccount({
    address_line1: next.addressLine1,
    address_line2: next.addressLine2,
    city: next.city,
    state: isUnitedStates(next.country) ? next.state : '',
    country: next.country,
    zip_code: next.zipCode,
  }).then(() => undefined);
}

/** Narrows field errors to those whose field the user has already interacted with (touched). */
export function visibleBillingErrors(
  fieldErrors: BillingFieldErrors,
  touched: ReadonlySet<string>,
): BillingFieldErrors {
  const visible: BillingFieldErrors = {};
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  for (const field of Object.keys(fieldErrors) as (keyof BillingFieldErrors)[]) {
    if (touched.has(field)) {
      visible[field] = fieldErrors[field];
    }
  }
  return visible;
}
